import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ApiError } from '~/api/errors';
import { ActorStringLabel } from '~/components/common/actor-string-label';
import { Button } from '~/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~/components/ui/dialog';
import { Skeleton } from '~/components/ui/skeleton';
import { DEFAULT_CATCHUP_WINDOW_SECONDS } from '~/features/catalog-operations/constants/schedules';
import {
  useScheduleDetail,
  useSchedulePreview,
  useUpdateSchedule,
} from '~/features/catalog-operations/hooks/use-catalog-ops-schedules';
import type { CadenceDraft } from '~/features/catalog-operations/types/catalog-ops-schedule.types';
import {
  cadenceDraftToSpec,
  validateCadence,
} from '~/features/catalog-operations/utils/schedule-cadence';
import { formatAuditTimestamp } from '~/utils/date';

import { ScheduleCadenceForm } from './schedule-cadence-form';

const PREVIEW_DEBOUNCE_MS = 450;

interface Props {
  /** Schedule being edited, or `null` when the dialog is closed. */
  scheduleId: string | null;
  onClose: () => void;
}

/**
 * Edits an existing schedule's cadence (`PUT /{id}`). Reuses the wizard's
 * cadence form, seeded from the stored spec so the dialog opens on the
 * schedule's current setting rather than a default.
 *
 * The config snapshot is untouched here — that is `PATCH /{id}/config`. A run
 * already in flight is unaffected; the change lands on the next fire.
 */
export function ScheduleCadenceDialog({ scheduleId, onClose }: Props) {
  const { t } = useTranslation('catalog-operations');
  // Shared actor labels ("Created by" / "Last updated by") live in the common ns.
  const { t: tc } = useTranslation('common');
  const { data: detail, isLoading } = useScheduleDetail(scheduleId);
  const preview = useSchedulePreview();
  const update = useUpdateSchedule(scheduleId ?? '');

  const [draft, setDraft] = useState<CadenceDraft | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);

  // Seed once per schedule: `detail.cadence` is the stored spec round-tripped
  // back into the form model.
  useEffect(() => {
    if (detail) setDraft(detail.cadence);
  }, [detail]);

  const cadenceInvalid = draft === null || validateCadence(draft) !== null;
  const specKey = useMemo(() => (draft ? JSON.stringify(cadenceDraftToSpec(draft)) : ''), [draft]);

  // Fire times come from the server for the edited spec, same as in the wizard.
  useEffect(() => {
    if (!detail || !draft || cadenceInvalid) return;
    const timer = setTimeout(() => {
      preview.mutate({ jobId: detail.sourceJobId, spec: cadenceDraftToSpec(draft) });
    }, PREVIEW_DEBOUNCE_MS);
    return () => {
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detail?.sourceJobId, cadenceInvalid, specKey]);

  function handleSave() {
    if (!draft) return;
    setServerError(null);
    update.mutate(
      {
        scheduleSpec: cadenceDraftToSpec(draft),
        ...(detail ? { name: detail.name, overlapPolicy: detail.overlapPolicy } : {}),
        catchupWindowSeconds: detail?.catchupWindowSeconds ?? DEFAULT_CATCHUP_WINDOW_SECONDS,
      },
      {
        onSuccess: onClose,
        onError: (err) => {
          // 400 belongs against the cadence control; anything else is surfaced
          // as-is so the user sees what the server actually said.
          setServerError(
            err instanceof ApiError && err.statusCode === 400
              ? t('schedules.errors.400.body')
              : err instanceof Error
                ? err.message
                : t('schedules.errors.generic.body'),
          );
        },
      },
    );
  }

  return (
    <Dialog
      open={scheduleId !== null}
      onOpenChange={(open) => {
        if (!open && !update.isPending) onClose();
      }}
    >
      <DialogContent
        data-testid="schedules-edit-cadence-dialog"
        className="flex max-h-[92vh] max-w-2xl flex-col gap-4 overflow-hidden"
      >
        <DialogHeader>
          <DialogTitle
            data-testid="schedules-edit-cadence-dialog-title"
            className="text-lg font-semibold"
          >
            {t('schedules.editCadence.title')}
          </DialogTitle>
          <DialogDescription>
            {detail
              ? t('schedules.editCadence.subtitle', { name: detail.name })
              : t('schedules.editCadence.subtitleFallback')}
          </DialogDescription>
        </DialogHeader>

        <div className="min-h-0 flex-1 overflow-y-auto pr-1">
          {isLoading || !draft ? (
            <div data-testid="schedules-edit-cadence-dialog-loading" className="space-y-3">
              {Array.from({ length: 4 }).map((_, i) => (
                <Skeleton key={i} className="h-12 w-full rounded-lg" />
              ))}
            </div>
          ) : (
            <>
              {detail?.live?.source === 'UNAVAILABLE' && (
                <div
                  data-testid="schedules-edit-cadence-dialog-live-unavailable-banner"
                  className="border-warning bg-warning-50 text-muted-foreground mb-4 rounded-lg border border-l-[3px] px-3 py-2.5 text-sm"
                >
                  {t('schedules.editCadence.liveUnavailable')}
                </div>
              )}
              <ScheduleCadenceForm
                testId="schedules-cadence"
                draft={draft}
                onChange={setDraft}
                nextRuns={preview.data?.nextRunPreview ?? []}
                isPreviewLoading={preview.isPending}
                serverError={serverError}
              />
              {/* Provenance strip: who created the schedule and who last changed
                  it, from the audit stamps on the detail DTO. */}
              {detail && (
                <div
                  data-testid="schedules-edit-cadence-dialog-provenance"
                  className="text-muted-foreground mt-4 space-y-1 border-t pt-3 text-xs"
                >
                  <div className="flex min-w-0 flex-wrap items-center gap-1.5">
                    <span>{tc('actor.createdBy')}</span>
                    <ActorStringLabel
                      value={detail.createdBy}
                      showAvatar={false}
                      className="text-xs"
                      testId="schedules-edit-cadence-dialog-created-by-actor"
                    />
                    <span aria-hidden>·</span>
                    <span className="tabular-nums">{formatAuditTimestamp(detail.createdAt)}</span>
                  </div>
                  <div className="flex min-w-0 flex-wrap items-center gap-1.5">
                    <span>{tc('actor.updatedBy')}</span>
                    <ActorStringLabel
                      value={detail.updatedBy}
                      showAvatar={false}
                      className="text-xs"
                      testId="schedules-edit-cadence-dialog-updated-by-actor"
                    />
                    <span aria-hidden>·</span>
                    <span className="tabular-nums">{formatAuditTimestamp(detail.updatedAt)}</span>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button
            data-testid="schedules-edit-cadence-dialog-cancel-button"
            variant="ghost"
            className="cursor-pointer"
            disabled={update.isPending}
            onClick={onClose}
          >
            {t('schedules.wizard.cancel')}
          </Button>
          <Button
            data-testid="schedules-edit-cadence-dialog-save-button"
            className="cursor-pointer disabled:cursor-not-allowed"
            isLoading={update.isPending}
            disabled={cadenceInvalid}
            onClick={handleSave}
          >
            {t('schedules.editCadence.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
